import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
// @mui
import { Alert, Box, Container, CircularProgress } from '@mui/material';
// hooks
import useAccessProvider from '../hooks/useAccessProvider';
// routes
import { PATH_ADMIN } from '../routes/paths';

// ----------------------------------------------------------------------
// Keeps provider-specific pages reachable only by orgs that run that provider.
//
// The backend refuses these calls anyway (see access_control/permissions.py),
// so this is about not showing an operator a page full of errors for a vendor
// they have never heard of — not about security.
// ----------------------------------------------------------------------

AccessProviderGuard.propTypes = {
  // Pass exactly one of these. `provider` names the vendor a page belongs to;
  // `capability` gates on a feature instead, so a provider that gains the
  // feature later opens the page with no change here.
  provider: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  capability: PropTypes.string,
  children: PropTypes.node,
  redirectTo: PropTypes.string,
};

export default function AccessProviderGuard({ provider, capability, children, redirectTo }) {
  const { provider: active, capabilities, loading, error } = useAccessProvider();

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Capability mode mirrors the nav rule in checkNavAccess: bounce only once we
  // know the provider and know it lacks the feature. An unresolved provider —
  // lookup failed, or the org never had one set — falls through to the page,
  // because locking an existing customer out is worse than a page they can't use.
  if (capability) {
    if (!error && active && !capabilities?.[capability]) {
      return <Navigate to={redirectTo || PATH_ADMIN.accessControl.users} replace />;
    }
    return <Box>{children}</Box>;
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const allowed = Array.isArray(provider) ? provider : [provider];
  if (active && allowed.includes(active)) {
    return <Box>{children}</Box>;
  }

  // No provider at all is a setup state rather than a wrong turn, so say so
  // instead of bouncing the operator somewhere with no explanation.
  if (!active) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="info">
          No access-control system is enabled for this organisation yet.
        </Alert>
      </Container>
    );
  }

  return <Navigate to={redirectTo || PATH_ADMIN.accessControl.users} replace />;
}
